// The "Map this value to..." choices offered when a value in the source tree is
// clicked: every rule the mapping already has (replace its source) plus the
// sensible NEW targets (a label named after the field, or a missing Signal
// name / Value / Time). Pure - the editor only renders and applies the choice.
import type { MappingRule } from './mappingEngine';

export interface MapTargetOption {
  key: string; // rule:<output>:<rule> | new:labels | new:name | new:value | new:at
  label: string;
  disabled: boolean;
  hint?: string;
}

const TITLES: Record<string, string> = { name: 'Signal name', value: 'Value', at: 'Time', signalRef: 'Signal ref' };

export function ruleTitle(to: string): string {
  if (TITLES[to]) return TITLES[to];
  if (to.startsWith('labels.')) return `Label · ${to.slice(7)}`;
  return to || 'field';
}

// The label key a field would get: its last path segment, made safe.
export function labelKeyFor(path: string): string {
  const leaf = path.split('.').filter(Boolean).pop() ?? 'field';
  return leaf.replace(/[^A-Za-z0-9_]/g, '_').replace(/^_+|_+$/g, '') || 'field';
}

function nowText(rule: MappingRule): string {
  if (rule.from) return `field ${rule.from}`;
  if (rule.const !== undefined) return `fixed “${String(rule.const)}”`;
  return 'not set';
}

// A value outside the records can only become fixed text, which only makes
// sense for a Signal name or a label.
const takesFixedText = (to: string) => to === 'name' || to.startsWith('labels.');

// outputs: the mapping's rule lists (one per output). insideRecords: the clicked
// value lives in the record list (so it can be a per-record `from`) - a boolean
// for all outputs, or per output when outputs read different lists.
export function buildMapTargets(outputs: MappingRule[][], path: string, inside: boolean | ((output: number) => boolean)): MapTargetOption[] {
  const insideOf = (o: number) => (typeof inside === 'function' ? inside(o) : inside);
  const anyInside = outputs.some((_, o) => insideOf(o)) || (outputs.length === 0 && insideOf(0));
  const out: MapTargetOption[] = [];
  const multi = outputs.length > 1;
  const have = new Set<string>();
  outputs.forEach((rules, o) =>
    rules.forEach((rule, i) => {
      const to = rule.to ?? '';
      have.add(to);
      const ok = insideOf(o) || takesFixedText(to);
      out.push({
        key: `rule:${o}:${i}`,
        label: `${multi ? `Output ${o + 1} · ` : ''}${ruleTitle(to)} - now: ${nowText(rule)}`,
        disabled: !ok,
        hint: ok ? undefined : 'needs a field inside the records',
      });
    }),
  );
  const leaf = labelKeyFor(path);
  if (!have.has(`labels.${leaf}`)) out.push({ key: 'new:labels', label: `New label “${leaf}”`, disabled: false });
  for (const to of ['name', 'value', 'at'] as const) {
    if (have.has(to)) continue;
    const ok = anyInside || to === 'name';
    out.push({ key: `new:${to}`, label: `New ${ruleTitle(to)}`, disabled: !ok, hint: ok ? undefined : 'needs a field inside the records' });
  }
  return out;
}
